import React, { useState } from 'react';
import {
  ArrowLeft,
  Users,
  UserPlus,
  Settings,
  Link2,
  Copy,
  Check,
  Crown,
  User,
  Trash2,
  LogOut,
  Edit2,
  MoreVertical,
  Mail,
  Clock,
  AlertCircle,
  RefreshCw,
  Share2,
  MessageCircle,
  Shield,
  ShieldOff,
  Database
} from 'lucide-react';
import { doc, setDoc, collection, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { useGroup } from '../../contexts/GroupContext';
import { usePresence, usePermissions } from '../../hooks/useFirebaseOptimized';
import { auth, db } from '../../firebase';
import toast from 'react-hot-toast';
import DataManagement from '../DataManagement';
import { AddMemberGate } from '../subscription/FeatureGates';


const GroupSettings = ({ onNavigate }) => {
  const user = auth.currentUser;
  const { 
    currentGroup, 
    userGroups,
    isAdmin,
    createInvite, 
    removeMember, 
    leaveGroup,
    updateGroupName,
    switchGroup
  } = useGroup();

  const { onlineMembers } = usePresence(user, currentGroup);
  const { permissions } = usePermissions(user, currentGroup);

  // Email invite function - stores pending invite by email
  const inviteByEmail = async (email) => {
    if (!currentGroup || !user) {
      throw new Error('Not authenticated');
    }

    const normalizedEmail = email.toLowerCase().trim();
    
    // Check if already a member
    const members = Object.values(currentGroup.members || {});
    if (members.some(m => m.email?.toLowerCase() === normalizedEmail)) {
      throw new Error('This person is already a member');
    }

    try {
      // Create email invite in the group's invites subcollection
      const inviteData = {
        email: normalizedEmail,
        groupId: currentGroup.id,
        groupName: currentGroup.name,
        role: 'member',
        type: 'email',
        status: 'pending',
        invitedBy: user.uid,
        invitedByEmail: user.email,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
      };

      await addDoc(collection(db, 'groups', currentGroup.id, 'invites'), inviteData);

      // Also store in a global email-invites collection for easy lookup on login
      await setDoc(doc(db, 'emailInvites', normalizedEmail), {
        groupId: currentGroup.id,
        groupName: currentGroup.name,
        role: 'member',
        invitedBy: user.uid,
        invitedByEmail: user.email,
        createdAt: new Date().toISOString()
      }, { merge: true });

      return { success: true, email: normalizedEmail };
    } catch (error) {
      console.error('Error creating email invite:', error);
      throw error;
    }
  };

  const [activeTab, setActiveTab] = useState('members');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(currentGroup?.name || '');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showDataManagement, setShowDataManagement] = useState(false);

  if (!currentGroup) {
    return (
      <div className="max-w-2xl mx-auto p-4 text-center py-20">
        <p className="text-gray-500 dark:text-gray-400">No band selected</p>
      </div>
    );
  }

  const members = Object.entries(currentGroup.members || {})
    .map(([id, data]) => ({
      id,
      odatauserId: id, // for presence matching
      ...data
    }))
    .filter(member => member.email || member.name || member.role); // Filter out empty/invalid entries

  const isOnline = (userId) => onlineMembers.some(m => m.odatauserId === userId || m.odataid === userId);

  const handleUpdateName = async () => {
    if (!newName.trim() || newName === currentGroup.name) {
      setEditingName(false);
      return;
    }
    try {
      await updateGroupName(newName.trim());
      setEditingName(false);
      toast.success('Band name updated!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await removeMember(memberId);
      toast.success('Member removed');
      setConfirmRemove(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await leaveGroup(currentGroup.id);
      toast.success('You left the band');
      onNavigate('dashboard');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteGroup = async () => {
    if (deleteConfirmText !== currentGroup.name) {
      toast.error('Please type the band name to confirm');
      return;
    }

    try {
      // Delete the group document
      await deleteDoc(doc(db, 'groups', currentGroup.id));
      
      // Also try to delete the data subcollection
      try {
        await deleteDoc(doc(db, 'groups', currentGroup.id, 'data', 'main'));
      } catch (e) {
        // Ignore if data doc doesn't exist
      }

      toast.success('Band deleted');
      setConfirmDelete(false);
      window.location.reload(); // Reload to refresh groups list
    } catch (err) {
      console.error('Error deleting group:', err);
      toast.error(err.message || 'Failed to delete band');
    }
  };

  // Change member role (promote/demote)
  const handleChangeRole = async (memberId, newRole) => {
    if (!isAdmin) {
      toast.error('Only admins can change roles');
      return;
    }

    // Prevent demoting yourself if you're the only admin
    if (memberId === user.uid && newRole === 'member') {
      const adminCount = members.filter(m => m.role === 'admin').length;
      if (adminCount <= 1) {
        toast.error('Cannot demote yourself. You are the only admin.');
        return;
      }
    }

    try {
      const groupRef = doc(db, 'groups', currentGroup.id);
      await updateDoc(groupRef, {
        [`members.${memberId}.role`]: newRole
      });

      toast.success(newRole === 'admin' ? 'Member promoted to admin' : 'Admin demoted to member');
      
      // Reload to refresh the group data
      window.location.reload();
    } catch (err) {
      console.error('Error changing role:', err);
      toast.error('Failed to change role');
    }
  };

  // Check if current user is the creator
  const isCreator = currentGroup?.createdBy === user?.uid;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-14 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onNavigate('dashboard')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
            
            <div className="flex-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="flex-1 px-3 py-1 border-2 border-blue-500 rounded-lg dark:bg-gray-700 dark:text-white"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                  />
                  <button
                    onClick={handleUpdateName}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setNewName(currentGroup.name);
                    }}
                    className="px-3 py-1 text-gray-600 dark:text-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                    {currentGroup.name}
                  </h1>
                  {isAdmin && (
                    <button
                      onClick={() => setEditingName(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {members.length} member{members.length !== 1 ? 's' : ''}
                {onlineMembers.length > 0 && ` • ${onlineMembers.length} online`}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 -mb-4">
            {['members', 'settings'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab
                    ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 border-t-2 border-x border-blue-500'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {tab === 'members' && <Users size={16} className="inline mr-1.5" />}
                {tab === 'settings' && <Settings size={16} className="inline mr-1.5" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {activeTab === 'members' && (
          <div className="space-y-4">
           {/* Invite Button */}
{permissions.canInviteMembers && (
  <AddMemberGate currentCount={members.length}>
    <button
      onClick={() => setShowInviteModal(true)}
      className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
    >
      <UserPlus size={20} />
      Invite Members
    </button>
  </AddMemberGate>
)}

            {/* Members List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {members
                .sort((a, b) => {
                  // Admins first, then alphabetical
                  if (a.role === 'admin' && b.role !== 'admin') return -1;
                  if (b.role === 'admin' && a.role !== 'admin') return 1;
                  return (a.email || '').localeCompare(b.email || '');
                })
                .map((member, index) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    isCurrentUser={member.id === user?.uid}
                    isOnline={isOnline(member.id)}
                    canRemove={permissions.canRemoveMembers && member.id !== user?.uid}
                    canChangeRole={isAdmin && member.id !== user?.uid}
                    onRemove={() => setConfirmRemove(member)}
                    onChangeRole={handleChangeRole}
                    showDivider={index < members.length - 1}
                  />
                ))}
            </div>

            {/* Leave Group */}
            {members.length > 1 && (
              <button
                onClick={() => setConfirmLeave(true)}
                className="w-full flex items-center justify-center gap-2 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
              >
                <LogOut size={18} />
                Leave Band
              </button>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Switch Bands */}
            {userGroups.length > 1 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-medium text-gray-800 dark:text-gray-100 mb-3">Your Bands</h3>
                <div className="space-y-2">
                  {userGroups.map(group => (
                    <button
                      key={group.id}
                      onClick={() => {
                        switchGroup(group.id);
                        toast.success(`Switched to ${group.name}`);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        group.id === currentGroup.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                        {group.name.charAt(0)}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-800 dark:text-gray-100">{group.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {Object.keys(group.members || {}).length} members
                        </p>
                      </div>
                      {group.id === currentGroup.id && (
                        <Check size={18} className="text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Band */}
            <button
              onClick={() => onNavigate('create-group')}
              className="w-full flex items-center justify-center gap-2 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <Users size={18} />
              Create Another Band
            </button>

            {/* Join Band */}
            <button
              onClick={() => onNavigate('join-group')}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-xl font-medium hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
            >
              <Link2 size={18} />
              Join Band with Code
            </button>

            {/* Data Management (Admin only) */}
            {isAdmin && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-gray-700 dark:text-gray-300 font-semibold mb-3 flex items-center gap-2">
                  <Database size={18} />
                  Data Management
                </h3>
                <button
                  onClick={() => setShowDataManagement(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded-xl font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <Database size={18} />
                  Manage Data & Storage
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  Clean up duplicates and optimize storage
                </p>
              </div>
            )}

            {/* Danger Zone - Delete Band (only for creator) */}
            {isCreator && (
              <div className="mt-8 pt-6 border-t border-red-200 dark:border-red-900">
                <h3 className="text-red-600 dark:text-red-400 font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle size={18} />
                  Danger Zone
                </h3>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                    Permanently delete <strong>{currentGroup.name}</strong> and all its data. This action cannot be undone.
                  </p>
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
                  >
                    <Trash2 size={18} />
                    Delete Band
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          createInvite={createInvite}
          onEmailInvite={inviteByEmail}
          groupName={currentGroup.name}
        />
      )}

      {/* Confirm Remove Modal */}
      {confirmRemove && (
        <ConfirmModal
          title="Remove Member?"
          message={`Are you sure you want to remove ${confirmRemove.email || confirmRemove.name || 'this member'} from ${currentGroup.name}? They will lose access to all band data.`}
          confirmLabel="Remove"
          confirmColor="red"
          onConfirm={() => handleRemoveMember(confirmRemove.id)}
          onCancel={() => setConfirmRemove(null)}
        />
      )}

      {/* Confirm Leave Modal */}
      {confirmLeave && (
        <ConfirmModal
          title="Leave Band?"
          message={`Are you sure you want to leave ${currentGroup.name}? You'll need a new invite to rejoin.`}
          confirmLabel="Leave"
          confirmColor="red"
          onConfirm={handleLeaveGroup}
          onCancel={() => setConfirmLeave(false)}
        />
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <Trash2 className="text-red-600 dark:text-red-400" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">Delete Band?</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This cannot be undone</p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                This will permanently delete <strong>{currentGroup.name}</strong> and all its data including:
              </p>
              
              <ul className="text-sm text-gray-500 dark:text-gray-400 mb-4 space-y-1">
                <li>• All shows and events</li>
                <li>• All expenses and financial data</li>
                <li>• All inventory items</li>
                <li>• All contacts and documents</li>
                <li>• All member access</li>
              </ul>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Type <strong>{currentGroup.name}</strong> to confirm:
              </p>
              
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={currentGroup.name}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-red-500 mb-4"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setConfirmDelete(false);
                    setDeleteConfirmText('');
                  }}
                  className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteGroup}
                  disabled={deleteConfirmText !== currentGroup.name}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Delete Forever
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Management Modal */}
      {showDataManagement && (
        <DataManagement onClose={() => setShowDataManagement(false)} />
      )}
    </div>
  );
};

// ============================================
// MEMBER ROW COMPONENT
// ============================================

const MemberRow = ({ member, isCurrentUser, isOnline, canRemove, canChangeRole, onRemove, onChangeRole, showDivider }) => {
  const [showMenu, setShowMenu] = useState(false);
  
  // Get display name - try email, then name, then ID
  const displayName = member.email || member.name || member.displayName || `User ${member.id?.slice(-6) || '?'}`;
  const initial = displayName?.charAt(0)?.toUpperCase() || '?';
  
  return (
    <div className={`flex items-center gap-3 p-4 ${showDivider ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}>
      {/* Avatar */}
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <span className="text-gray-600 dark:text-gray-300 font-medium">
            {initial}
          </span>
        </div>
        {isOnline && (
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800 dark:text-gray-100 truncate">
            {displayName}
          </span>
          {isCurrentUser && (
            <span className="text-xs text-gray-400">(you)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            member.role === 'admin'
              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            {member.role === 'admin' ? <Crown size={10} /> : <User size={10} />}
            {member.role === 'admin' ? 'Admin' : 'Member'}
          </span>
          {isOnline && (
            <span className="text-xs text-green-600 dark:text-green-400">Online</span>
          )}
        </div>
      </div>

      {/* Actions */}
      {(canChangeRole || canRemove) && !isCurrentUser && (
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <MoreVertical size={18} />
          </button>
          
          {showMenu && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowMenu(false)}
              />
              
              {/* Menu */}
              <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 min-w-[160px] z-20">
                {canChangeRole && (
                  <button
                    onClick={() => {
                      onChangeRole(member.id, member.role === 'admin' ? 'member' : 'admin');
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {member.role === 'admin' ? (
                      <>
                        <ShieldOff size={16} className="text-amber-500" />
                        <span>Demote to Member</span>
                      </>
                    ) : (
                      <>
                        <Shield size={16} className="text-purple-500" />
                        <span>Make Admin</span>
                      </>
                    )}
                  </button>
                )}
                
                {canRemove && (
                  <button
                    onClick={() => {
                      onRemove();
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 size={16} />
                    <span>Remove</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// INVITE MODAL COMPONENT
// ============================================

const InviteModal = ({ onClose, createInvite, groupName, onEmailInvite }) => {
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('email'); // 'email' or 'link'
  const [email, setEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [invitedEmails, setInvitedEmails] = useState([]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await createInvite();
      setInviteLink(result.link);
      setInviteCode(result.code);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setEmailLoading(true);
    try {
      await onEmailInvite(email.trim().toLowerCase());
      setInvitedEmails([...invitedEmails, email.trim().toLowerCase()]);
      setEmail('');
      toast.success(`Invite sent to ${email}!`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <UserPlus className="text-blue-600 dark:text-blue-400" size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 dark:text-gray-100">Invite to {groupName}</h3>
              <p className="text-xs text-gray-500">Add band members</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <AlertCircle size={20} className="text-gray-500 rotate-45" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Mail size={16} className="inline mr-2" />
            By Email
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'link'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Link2 size={16} className="inline mr-2" />
            Share Link
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'email' ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter their email address. When they sign up, they'll automatically see your invite!
              </p>
              
              <form onSubmit={handleEmailInvite} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="bandmate@email.com"
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={emailLoading}
                />
                <button
                  type="submit"
                  disabled={emailLoading || !email.trim()}
                  className="px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {emailLoading ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <UserPlus size={18} />
                  )}
                </button>
              </form>

              {invitedEmails.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Invites sent:</p>
                  {invitedEmails.map((invEmail, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <Check size={14} />
                      {invEmail}
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  💡 They'll need to sign up with this exact email to see the invite
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {!inviteLink ? (
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Link2 size={18} />
                      Generate Invite Link
                    </>
                  )}
                </button>
              ) : (
                <>
                  {/* Invite Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Invite Code
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-900 rounded-lg font-mono text-xl text-center tracking-widest font-bold text-gray-800 dark:text-gray-100">
                        {inviteCode}
                      </div>
                      <button
                        onClick={() => handleCopy(inviteCode)}
                        className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Invite Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Or share this link
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded-lg text-sm font-mono text-gray-600 dark:text-gray-400"
                      />
                      <button
                        onClick={() => handleCopy(inviteLink)}
                        className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                        title="Copy link"
                      >
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Share Options */}
                  <div className="grid grid-cols-3 gap-2">
                    {/* Text/SMS Share */}
                    <a
                      href={`sms:?&body=${encodeURIComponent(`Join my band on Boply! \n\nUse code: ${inviteCode}\n\nOr click: ${inviteLink}`)}`}
                      className="flex flex-col items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                    >
                      <MessageCircle size={24} />
                      <span className="text-xs font-medium">Text</span>
                    </a>

                    {/* Email Share */}
                    <a
                      href={`mailto:?subject=${encodeURIComponent(`Join my band on Boply!`)}&body=${encodeURIComponent(`Hey!\n\nI'm inviting you to join my band on Boply! 🎸\n\nUse this code: ${inviteCode}\n\nOr click this link: ${inviteLink}\n\nSee you there!`)}`}
                      className="flex flex-col items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <Mail size={24} />
                      <span className="text-xs font-medium">Email</span>
                    </a>

                    {/* Native Share (Mobile) */}
                    <button
                      onClick={async () => {
                        if (navigator.share) {
                          try {
                            await navigator.share({
                              title: 'Join my band on Boply!',
                              text: `Join my band on Boply! \n\nUse code: ${inviteCode}`,
                              url: inviteLink
                            });
                          } catch (err) {
                            if (err.name !== 'AbortError') {
                              handleCopy(inviteLink);
                            }
                          }
                        } else {
                          handleCopy(inviteLink);
                        }
                      }}
                      className="flex flex-col items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                    >
                      <Share2 size={24} />
                      <span className="text-xs font-medium">Share</span>
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    <Clock size={12} className="inline mr-1" />
                    Expires in 7 days
                  </p>

                  <button
                    onClick={handleGenerate}
                    className="w-full py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm"
                  >
                    Generate New Code
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// CONFIRM MODAL COMPONENT
// ============================================

const ConfirmModal = ({ title, message, confirmLabel, confirmColor, onConfirm, onCancel }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full shadow-2xl p-6">
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
        
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`flex-1 py-2 rounded-lg font-medium text-white ${
              confirmColor === 'red' 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            } disabled:opacity-50`}
          >
            {loading ? 'Please wait...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupSettings;